import { useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import { useReopenOS } from '@/hooks/useServiceOrders';
import { STATUS_MAP } from '@/lib/status';

const MIN_REASON = 5;

/**
 * "Reabrir OS" — o gestor desfaz o fechamento por engano (concluída ou
 * entregue) em até 48h. Só aparece quando a API devolve `reopen` no detalhe
 * da OS; o destino vem de lá também (o status anterior ao fechamento), o
 * gestor não escolhe. Regra: `apps/api/src/lib/os-reopen.ts`.
 */
export function ReopenOSButton({
  osId,
  osNumber,
  reopen,
}: {
  osId: string;
  osNumber: number;
  reopen: { targetStatus: string; until: string };
}) {
  const { toast } = useToast();
  const { reopen: submit, isReopening } = useReopenOS(osId);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const targetLabel = STATUS_MAP[reopen.targetStatus]?.label ?? reopen.targetStatus;
  const until = new Date(reopen.until).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const reasonOk = reason.trim().length >= MIN_REASON;

  const close = () => {
    if (isReopening) return;
    setOpen(false);
    setReason('');
    setError('');
  };

  const confirm = async () => {
    setError('');
    try {
      await submit({ reason: reason.trim() });
      toast({ type: 'success', title: `OS #${osNumber} reaberta`, description: `Voltou para "${targetLabel}".` });
      setOpen(false);
      setReason('');
    } catch (err) {
      setError(formatApiError(err, 'Não foi possível reabrir a OS. Tente novamente.'));
    }
  };

  return (
    <>
      <button
        type="button"
        className="flex h-8 items-center gap-1.5 rounded-md border border-input px-3 text-sm font-medium hover:bg-muted"
        onClick={() => setOpen(true)}
        title={`Disponível até ${until}`}
      >
        <RotateCcw size={14} />
        Reabrir OS
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
          onClick={close}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reopen-os-title"
            className="w-full max-w-md rounded-t-xl bg-background p-6 shadow-xl sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="reopen-os-title" className="text-lg font-semibold">
              Reabrir OS #{osNumber}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              A OS volta para <strong className="text-foreground">{targetLabel}</strong>, o status em que
              estava antes de fechar. Use só para desfazer um fechamento por engano — se o carro voltou
              com problema, abra uma OS nova.
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              <li>Disponível até {until} (48h depois do fechamento).</li>
              <li>A data de entrega e, se a OS voltar para o trabalho, a de conclusão são apagadas.</li>
              <li>Pagamentos registrados continuam valendo. O cliente não recebe notificação.</li>
            </ul>
            <label htmlFor="reopen-os-reason" className="mt-4 block text-sm font-medium">
              Motivo
            </label>
            <textarea
              id="reopen-os-reason"
              className="mt-1 block w-full rounded-lg border border-input bg-background p-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ex.: marquei como entregue a OS errada"
              rows={3}
              maxLength={500}
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isReopening}
            />
            <p className="mt-1 text-xs text-muted-foreground">Obrigatório · fica registrado no histórico da OS.</p>
            {error && (
              <p role="alert" className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
                onClick={close}
                disabled={isReopening}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity disabled:opacity-50"
                disabled={!reasonOk || isReopening}
                onClick={() => void confirm()}
              >
                {isReopening && <Loader2 size={14} className="animate-spin" />}
                Reabrir OS
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
